import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './App.css';
import { useTheme } from './ThemeContext';
import SunIcon from './images/sun.png';
import MoonIcon from './images/moon.png';
import { db } from './FBConfigs';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import Img1 from './images/1.png';
import Img2 from './images/2.png';
import Img3 from './images/3.png';

function App() {
  const { theme, toggleTheme } = useTheme();
  const [userType, setUserType] = useState('Patient');
  const [patientId, setPatientId] = useState('');
  const navigate = useNavigate();
  const ehrSectionRef = useRef(null);

  // =========================== STATIC NEWS ITEMS ===========================
  const staticNewsItems = [
    "Breakthrough in Cancer Immunotherapy!",
    "New Vaccine Shows Promise Against Flu Strains.",
    "Innovative AI Model Improves Radiology Diagnoses.",
    "Telehealth Services Expanding Globally.",
    "Robotics Assisting in Complex Surgeries.",
    "Research Links Exercise to Reduced Dementia Risk.",
    "Breakthrough in Heart Disease Treatment.",
    "Mental Health Awareness Growing Worldwide.",
    "Smart Wearables Monitoring Chronic Illnesses.",
    "Advances in Genetic Engineering.",
    "New Hope for Diabetes Management.",
    "Modern Medical Devices Enhance Recovery.",
    "Breakthroughs in Regenerative Medicine.",
    "Personalized Medicine Redefines Patient Care.",
    "Emerging Therapies in Neurodegenerative Diseases."
  ];

  // =========================== HANDLERS ===========================
  const handleUserTypeChange = (type) => {
    setUserType(type);
    setPatientId('');
  };
  
  const handleAccess = () => {
    ehrSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const handleFinalAccess = async () => {
    if (!patientId.trim()) {
      Swal.fire({
        icon: 'warning',
        title: `${userType} ID required`,
        text: 'Please enter a valid ID to proceed.'
      });
      return;
    }

    try {
      await addDoc(collection(db, 'ehrAccessLogs'), {
        userType,
        patientId,
        accessedAt: new Date()
      });
      const collectionName = userType === 'Patient' ? 'patients' : 'doctors';
      const q = query(collection(db, collectionName), where('uniqueId', '==', patientId.trim()));
      const querySnap = await getDocs(q);

      if (!querySnap.empty) {
        const doc = querySnap.docs[0];
        const data = { id: doc.id, ...doc.data() };
        Swal.fire({
          icon: 'success',
          title: 'Record Found',
          text: `Data for ${data.fullName} has been retrieved.`
        });
        navigate('/medical-record', { state: { fetchedData: data } }); 
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Not Found',
          text: `No ${userType.toLowerCase()} found with ID: ${patientId}`
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Database Error',
        text: 'Could not fetch patient data. Try again later.'
      });
    }
  };
  
  const handleAdminAccess = () => {
    navigate('/admin-update');
  };
  
  return (
    <div className={`app ${theme}`}>

      <nav className="navbar">
        <div className="logo">MedVault</div>
        <ul className="nav-links">
          <li><a href="#footya">About</a></li>
          <li><a href="/login">Login</a></li>
          <li><a href="/register">Register</a></li>
        </ul>
        <button onClick={toggleTheme} className="theme-toggle">
          <img
            src={theme === 'dark' ? SunIcon : MoonIcon}
            alt={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            className="theme-icon"
          />
        </button>
      </nav>

      {/* =========================== Medical News Marquee =========================== */}
      <section className="news-marquee">
        <div className="news-scroll">
          {[...staticNewsItems, ...staticNewsItems].map((item, idx) => (
            <span key={idx}>{item} &nbsp;|&nbsp;</span>
          ))}
        </div>
      </section>

      {/* =========================== Image Slideshow =========================== */}
      <section className="image-slideshow">
        <div className="slides">
          <img src={Img1} alt="Medical Scene 1" />
          <img src={Img2} alt="Medical Scene 2" />
          <img src={Img3} alt="Medical Scene 3" />
        </div>
        <div className="center-button-container">
          <button className="access-btn" onClick={handleAccess}>
            Access Electronic Health Record
          </button>
        </div>
      </section>

      {/* =========================== EHR Section =========================== */}
      <section className="ehr-section" ref={ehrSectionRef}>
        <h2>Electronic Health Record (EHR)</h2>
        <p>Welcome to your secure, paperless Electronic Health Record system.
           Here you can view and manage your medical history, prescriptions, and test results.
        </p>

        <div className="user-type-section">
          <label className="user-type-label">User Type</label>
          <div className="user-type-toggle wide-toggle">
            <button
              className={`toggle-btn ${userType === 'Patient' ? 'active' : ''}`}
              onClick={() => handleUserTypeChange('Patient')}
            >
              Patient
            </button>
            <button
              className={`toggle-btn ${userType === 'Doctor' ? 'active' : ''}`}
              onClick={() => handleUserTypeChange('Doctor')}
            >
              Doctor
            </button>
          </div>
        </div>

        <div className="ehr-access-form">
          <label htmlFor="patientId"><strong>{userType} ID:</strong></label>
          <input
            id="patientId"
            type="text"
            placeholder={userType === 'Patient' ? 'ABC1234567XYZ' : 'XYZ1234567ABC'}
            value={patientId}
            onChange={(e) => setPatientId(e.target.value.toUpperCase())}
          />
          <button className="access-btn" onClick={handleFinalAccess}>
            Access Data
          </button>
        </div>

        <div className="security-notice">
          <h4>Security Notice</h4>
          <p>All data access is logged and monitored for security purposes.
             Only authorized personnel should access this system.
          </p>
        </div>

        <div className="admin-access-button">
          <button className="access-btn" onClick={handleAdminAccess}>
            Update EHR (Admin)
          </button>
        </div>
      </section>

      <footer className="medical-footer" id="footya">
        <p>&copy; 2025 EHR Medical Systems. All rights reserved.</p>
        <p>Contact us: support@ehrmedical.com | +91 9xxxxx-xxxx</p>
      </footer>
    </div>
  );
}

export default App;
